import { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { CompanyRoleTable, CompanyTable } from "../CompanyTable";
import { MembersActionBlock } from "./MembersActionBlock";
import { MembersUpdateValues } from "../../../types/companyTypes";
import { TabsBlock } from "./TabsBlock";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { setIsFetchTableMembers } from "../../../features/companySlice";
import { campaignsApi, useGetCampaignRoleMembersQuery } from "../../../api";

const DEFAULT_TAB = "role";

export const MembersBlock = ({
  initCommitteeSelected,
  initClusterSelected,
}: {
  initCommitteeSelected: number;
  initClusterSelected: number;
}) => {
  const { account_id } = useParams();
  const dispatch = useAppDispatch();
  const isFetchTableMembers = useAppSelector(
    (state) => state.company.isFetchTableMembers,
  );
  const [page, setPage] = useState<number>(0);
  const [currentTab, setCurrentTab] = useState<string>(DEFAULT_TAB);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const [committeeSelected, setCommitteeSelected] = useState<number>(
    initCommitteeSelected,
  );
  const [clusterSelected, setClusterSelected] =
    useState<number>(initClusterSelected);

  const [updatedValues, setUpdatedValues] = useState<{
    [key: string]: MembersUpdateValues;
  }>({});

  const { data: roleViewData } = useGetCampaignRoleMembersQuery(
    { account_id: account_id! },
    { skip: !account_id },
  );

  const personas = useMemo(() => {
    if (!roleViewData?.roles) return [];
    const seen = new Set<string>();
    return roleViewData.roles.flatMap((role) =>
      role.personas
        .filter((p) => !seen.has(p.id) && seen.add(p.id))
        .map((p) => ({ id: p.id, name: p.name, actual_count: p.actual_count })),
    );
  }, [roleViewData]);

  useEffect(() => {
    if (isFetchTableMembers) {
      if (page === 0) {
        dispatch(campaignsApi.util.invalidateTags(["members", "role-members"]));
      } else {
        setPage(0);
      }
      dispatch(setIsFetchTableMembers(false));
    }
  }, [isFetchTableMembers]);

  useEffect(() => {
    if (currentTab === DEFAULT_TAB) setPage(0);
  }, [currentTab]);

  const updateHandler = useCallback(
    (
      values?: MembersUpdateValues,
      allValues?: {
        [key: string]: MembersUpdateValues;
      },
    ) => {
      if (values) {
        setUpdatedValues((prev) => {
          let updated = prev[values.member_id];
          if (updated) {
            updated = { ...updated, ...values };
            return { ...prev, [updated.member_id]: updated };
          }
          return { ...prev, [values.member_id]: values };
        });
      }
      if (allValues) setUpdatedValues(allValues);
    },
    [],
  );

  const onDiscardHandler = () => {
    setClusterSelected(initClusterSelected);
    setCommitteeSelected(initCommitteeSelected);
    setUpdatedValues({});
  };

  return (
    <Stack gap={3}>
      <Typography
        variant="h4"
        color="custom.textDarkBlue"
        sx={{ fontSize: "16px", lineHeight: "24px", fontWeight: 500 }}
      >
        Buying Committee & Cluster Members Identified
      </Typography>
      <Stack display="flex" flexDirection="column" gap="24px">
        <Stack flexDirection="row" gap="24px" alignItems="center">
          {!isEditMode && (
            <TabsBlock currentTab={currentTab} setCurrentTab={setCurrentTab} />
          )}

          <MembersActionBlock
            onSaveChanges={() => setUpdatedValues({})}
            onDiscard={onDiscardHandler}
            committeeSelected={committeeSelected}
            clusterSelected={clusterSelected}
            updatedValues={updatedValues}
            page={page}
            setPage={setPage}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            personas={personas}
          />
        </Stack>

        {currentTab === DEFAULT_TAB ? (
          <CompanyRoleTable
            setPage={setPage}
            account_id={account_id!}
            setCommitteeSelected={setCommitteeSelected}
            setClusterSelected={setClusterSelected}
            updatedValues={updatedValues}
            isEditMode={isEditMode}
            updateHandler={updateHandler}
          />
        ) : (
          <CompanyTable
            setCommitteeSelected={setCommitteeSelected}
            setClusterSelected={setClusterSelected}
            updatedValues={updatedValues}
            updateHandler={updateHandler}
            page={page}
            setPage={setPage}
            account_id={account_id!}
            isEditMode={isEditMode}
          />
        )}
      </Stack>
    </Stack>
  );
};
